import { useState, type FormEvent } from 'react';

import { Button, Card, Dropdown, Field, Input, MonoLabel } from '@/design-system';
import {
    formatMoney,
    formatPercentage,
    isTariffInForce,
    toDecimalAmount,
    type NewTariff,
    type Tariff,
    type TariffService,
} from '@/entities/invoice';
import { formatDayLong } from '@/shared/lib';

import { billingCopy } from '../copy';
import { describeBillingError } from '../errors';
import { useCreateTariff, useTariffServices, useUpdateTariff } from '../hooks';

export interface TariffsCardProps {
    tariffs: readonly Tariff[];
    /** Tarieven publiceren en wijzigen is voorbehouden aan de eigenaar. */
    canManage: boolean;
    /** Vandaag als `YYYY-MM-DD`; verlopen tariefversies vallen weg. */
    today: string;
}

/**
 * Tarieven per dienst (scherm 10, rechterkolom), met de vergoeding voor de
 * inspecteur en de marge die overblijft.
 *
 * Een bedrag wijzig je nooit op een bestaand tarief: keuringen zijn al tegen dat
 * bedrag aangenomen. Een ander bedrag is een nieuw tarief met een ingangsdatum;
 * het lopende tarief stopt dan de dag ervoor. Van een bestaand tarief kun je
 * alleen de naam en de laatste dag aanpassen.
 */
export function TariffsCard({ tariffs, canManage, today }: TariffsCardProps) {
    const [adding, setAdding] = useState<Partial<NewTariff> | null>(null);
    const [editingUuid, setEditingUuid] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const inForce = tariffs.filter((tariff) => isTariffInForce(tariff, today));

    return (
        <Card padding="md" as="section" aria-labelledby="tariffs-title">
            <h2
                id="tariffs-title"
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {billingCopy.tariffsTitle}
            </h2>

            {inForce.length === 0 ? (
                <p className="m-0 py-[9px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {billingCopy.tariffsEmpty}
                </p>
            ) : (
                <ul className="m-0 list-none p-0">
                    {inForce.map((tariff) => (
                        <li
                            key={tariff.uuid}
                            className="border-b border-[var(--border-subtle-2)] py-[9px]"
                        >
                            <TariffRow
                                tariff={tariff}
                                today={today}
                                canManage={canManage}
                                onEdit={() => {
                                    setEditingUuid(tariff.uuid);
                                    setAdding(null);
                                    setNotice(null);
                                }}
                            />
                            {editingUuid === tariff.uuid ? (
                                <EditTariffForm
                                    tariff={tariff}
                                    onDone={(message) => {
                                        setEditingUuid(null);
                                        setNotice(message);
                                    }}
                                    onNewAmount={() => {
                                        setEditingUuid(null);
                                        setAdding({
                                            serviceUuid: tariff.serviceUuid ?? undefined,
                                            name: tariff.name,
                                            vatRate: tariff.vatRate,
                                        });
                                    }}
                                />
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}

            {notice ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}

            {canManage ? (
                adding ? (
                    <NewTariffForm
                        initial={adding}
                        onDone={(message) => {
                            setAdding(null);
                            setNotice(message);
                        }}
                        onCancel={() => setAdding(null)}
                    />
                ) : (
                    <Button
                        variant="outline"
                        size="md"
                        fullWidth
                        icon="add"
                        className="mt-3"
                        onClick={() => {
                            setAdding({});
                            setEditingUuid(null);
                            setNotice(null);
                        }}
                    >
                        {billingCopy.addTariff}
                    </Button>
                )
            ) : null}
        </Card>
    );
}

function TariffRow({
    tariff,
    today,
    canManage,
    onEdit,
}: {
    tariff: Tariff;
    today: string;
    canManage: boolean;
    onEdit: () => void;
}) {
    const validity = [
        tariff.validFrom > today ? billingCopy.tariffFrom(formatDayLong(tariff.validFrom)) : null,
        tariff.validUntil ? billingCopy.tariffUntil(formatDayLong(tariff.validUntil)) : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <>
            <div className="flex items-center justify-between gap-[10px]">
                <span className="min-w-0 flex-1 truncate text-[13px] leading-[1.4] text-[var(--text-primary)]">
                    {tariff.name}
                </span>
                <MonoLabel size={11} variant="value" tone="default">
                    {formatMoney(tariff.price)}
                </MonoLabel>
            </div>
            <div className="mt-[3px] flex items-center justify-between gap-[10px]">
                <span className="text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                    {billingCopy.inspectorFee}
                </span>
                <MonoLabel size={10} variant="value" tone="muted">
                    {formatMoney(tariff.inspectorFee)}
                </MonoLabel>
            </div>
            <div className="mt-[3px] flex flex-wrap items-center justify-between gap-x-[10px] gap-y-1">
                <span className="min-w-0 text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                    {billingCopy.tariffDetails(
                        tariff.serviceName,
                        formatMoney(tariff.margin),
                        formatPercentage(tariff.vatRate),
                    )}
                    {validity ? ` · ${validity}` : ''}
                </span>
                {canManage ? (
                    <Button
                        variant="ghost"
                        size="xs"
                        icon="edit"
                        aria-label={billingCopy.editTariffLabel(tariff.name)}
                        onClick={onEdit}
                    >
                        {billingCopy.editTariff}
                    </Button>
                ) : null}
            </div>
        </>
    );
}

function NewTariffForm({
    initial,
    onDone,
    onCancel,
}: {
    initial: Partial<NewTariff>;
    onDone: (message: string) => void;
    onCancel: () => void;
}) {
    const services = useTariffServices(true);
    const create = useCreateTariff();
    const [serviceUuid, setServiceUuid] = useState(initial.serviceUuid ?? '');
    const [name, setName] = useState(initial.name ?? '');
    const [price, setPrice] = useState('');
    const [fee, setFee] = useState('');
    const [vatRate, setVatRate] = useState(initial.vatRate ? toVatInput(initial.vatRate) : '21');
    const [validFrom, setValidFrom] = useState('');
    const [serviceMissing, setServiceMissing] = useState(false);

    function submit(event: FormEvent) {
        event.preventDefault();

        /* De keuzelijst is geen native select: "verplicht" controleren we zelf. */
        if (serviceUuid === '') {
            setServiceMissing(true);

            return;
        }

        const input: NewTariff = {
            serviceUuid,
            name: name.trim(),
            price: toDecimalAmount(price),
            inspectorFee: toDecimalAmount(fee),
            vatRate: toDecimalAmount(vatRate),
            validFrom: validFrom || null,
        };

        create.mutate(input, {
            onSuccess: (tariff) => onDone(billingCopy.tariffSaved(tariff.name)),
        });
    }

    return (
        <form
            onSubmit={submit}
            aria-label={billingCopy.addTariff}
            className="mt-3 grid gap-[10px] border-t border-[var(--border-subtle-2)] pt-3"
        >
            <Field
                label={billingCopy.tariffService}
                size="sm"
                error={serviceMissing ? billingCopy.tariffServiceMissing : undefined}
            >
                <Dropdown
                    value={serviceUuid}
                    placeholder={billingCopy.tariffServicePick}
                    options={(services.data ?? []).map((service: TariffService) => ({
                        value: service.uuid,
                        label: service.name,
                    }))}
                    onChange={(next) => {
                        const service = services.data?.find((item) => item.uuid === next);

                        setServiceUuid(next);
                        setServiceMissing(false);

                        if (name.trim() === '' && service) {
                            setName(service.name);
                        }
                    }}
                />
            </Field>
            <Field label={billingCopy.tariffName} size="sm">
                <Input value={name} required onChange={(event) => setName(event.target.value)} />
            </Field>
            <Field
                label={billingCopy.tariffPrice}
                size="sm"
                description={billingCopy.tariffAmountHelp}
            >
                <Input
                    mono
                    inputMode="decimal"
                    value={price}
                    required
                    onChange={(event) => setPrice(event.target.value)}
                />
            </Field>
            <Field label={billingCopy.inspectorFee} size="sm">
                <Input
                    mono
                    inputMode="decimal"
                    value={fee}
                    required
                    onChange={(event) => setFee(event.target.value)}
                />
            </Field>
            <Field label={billingCopy.tariffVat} size="sm">
                <Input
                    mono
                    inputMode="decimal"
                    value={vatRate}
                    required
                    onChange={(event) => setVatRate(event.target.value)}
                />
            </Field>
            <Field
                label={billingCopy.tariffValidFrom}
                size="sm"
                description={billingCopy.tariffValidFromHelp}
            >
                <Input
                    type="date"
                    value={validFrom}
                    onChange={(event) => setValidFrom(event.target.value)}
                />
            </Field>

            {create.isError ? (
                <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {describeBillingError(create.error)}
                </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="primary" size="sm" disabled={create.isPending}>
                    {billingCopy.saveTariff}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                    {billingCopy.cancel}
                </Button>
            </div>
        </form>
    );
}

/** `"21.00"` -> `"21"`, `"9.50"` -> `"9,5"`: zoals je het zelf zou typen. */
function toVatInput(rate: string): string {
    return formatPercentage(rate).replace('%', '');
}

function EditTariffForm({
    tariff,
    onDone,
    onNewAmount,
}: {
    tariff: Tariff;
    onDone: (message: string | null) => void;
    onNewAmount: () => void;
}) {
    const update = useUpdateTariff();
    const [name, setName] = useState(tariff.name);
    const [validUntil, setValidUntil] = useState(tariff.validUntil ?? '');

    function submit(event: FormEvent) {
        event.preventDefault();

        const nextName = name.trim();
        const nextUntil = validUntil === '' ? null : validUntil;
        const changes = {
            ...(nextName !== tariff.name ? { name: nextName } : {}),
            ...(nextUntil !== tariff.validUntil ? { validUntil: nextUntil } : {}),
        };

        if (Object.keys(changes).length === 0) {
            onDone(null);

            return;
        }

        update.mutate(
            { tariffUuid: tariff.uuid, changes },
            { onSuccess: (saved) => onDone(billingCopy.tariffSaved(saved.name)) },
        );
    }

    return (
        <form
            onSubmit={submit}
            aria-label={billingCopy.editTariffLabel(tariff.name)}
            className="mt-2 grid gap-[10px] rounded-[9px] bg-[var(--surface-subtle)] p-3"
        >
            <Field label={billingCopy.tariffName} size="sm">
                <Input value={name} required onChange={(event) => setName(event.target.value)} />
            </Field>
            <Field
                label={billingCopy.tariffValidUntil}
                size="sm"
                description={billingCopy.tariffValidUntilHelp}
            >
                <Input
                    type="date"
                    value={validUntil}
                    onChange={(event) => setValidUntil(event.target.value)}
                />
            </Field>

            {update.isError ? (
                <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {describeBillingError(update.error)}
                </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="primary" size="sm" disabled={update.isPending}>
                    {billingCopy.save}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => onDone(null)}>
                    {billingCopy.cancel}
                </Button>
            </div>

            <div className="flex flex-wrap items-center gap-x-2">
                <span className="text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {billingCopy.tariffAmountLocked}
                </span>
                <Button type="button" variant="ghost" size="xs" onClick={onNewAmount}>
                    {billingCopy.newAmount}
                </Button>
            </div>
        </form>
    );
}
